import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Button,
    Stack,
    useMediaQuery,
    useTheme,
    Drawer,
    Paper,
    Autocomplete,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TuneIcon from '@mui/icons-material/Tune';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import {
    ItemType,
    ItemTypeWithLocations,
    queryItemTypes,
    queryItemTypesWithLocations,
    queryInventoryHistory,
    InventoryHistory,
} from '../schema';
import { calculateItemLocations } from '../utils/inventory';
import { useNavigate } from 'react-router-dom';
import InventoryHistoryList from './InventoryHistoryList';
import { useMemo, useCallback } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { db } from '../instant';
import { id as instantId } from '@instantdb/react';
import { TRAILERS_CATEGORY_NAME } from '../utils/trailerCargoBan';

// History Summary Component
function HistorySummary({ itemTypeId }: { itemTypeId: string }) {
    const { data } = queryInventoryHistory(itemTypeId);
    const history = useMemo(() => (data?.inventoryHistory || []) as InventoryHistory[], [data?.inventoryHistory]);

    const summary = useMemo(() => {
        const purchases = history.filter(h => h.type === 'purchase').reduce((sum, h) => sum + h.quantityChange, 0);
        const losses = history
            .filter(h => h.type === 'lost' || h.type === 'broken')
            .reduce((sum, h) => sum + Math.abs(h.quantityChange), 0);
        const consumed = history.filter(h => h.type === 'consumed').reduce((sum, h) => sum + Math.abs(h.quantityChange), 0);
        const adjustments = history.filter(h => h.type === 'adjustment').reduce((sum, h) => sum + h.quantityChange, 0);
        const netChange = purchases - losses - consumed + adjustments;

        return { purchases, losses, consumed, adjustments, netChange };
    }, [history]);

    if (history.length === 0) {
        return null;
    }

    return (
        <Box component={Paper} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        Total Purchases
                    </Typography>
                    <Typography variant="h6" color="success.main">
                        +{summary.purchases}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        Total Losses
                    </Typography>
                    <Typography variant="h6" color="error.main">
                        -{summary.losses}
                    </Typography>
                </Box>
                {summary.consumed > 0 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Total Consumed
                        </Typography>
                        <Typography variant="h6" color="warning.main">
                            -{summary.consumed}
                        </Typography>
                    </Box>
                )}
                {summary.adjustments !== 0 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Adjustments
                        </Typography>
                        <Typography variant="h6" color="info.main">
                            {summary.adjustments >= 0 ? '+' : ''}
                            {summary.adjustments}
                        </Typography>
                    </Box>
                )}
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        Net Change
                    </Typography>
                    <Typography variant="h6" color={summary.netChange >= 0 ? 'success.main' : 'error.main'}>
                        {summary.netChange >= 0 ? '+' : ''}
                        {summary.netChange}
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
}

interface Props {
    itemType: ItemType | null;
    open: boolean;
    onClose: () => void;
    onEdit?: (itemType: ItemType) => void;
    onDelete?: (itemType: ItemType) => void;
    onRecordPurchase?: (itemType: ItemType) => void;
    onRecordLostOrBroken?: (itemType: ItemType) => void;
    onRecordConsumption?: (itemType: ItemType) => void;
    onAdjustQuantity?: (itemType: ItemType) => void;
}

export default function ItemTypeDetail({
    itemType,
    open,
    onClose,
    onEdit,
    onDelete,
    onRecordPurchase,
    onRecordLostOrBroken,
    onRecordConsumption,
    onAdjustQuantity,
}: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const stableItemId = itemType?.id ?? '';
    const { canManageInventory } = usePermissions();
    const { data: itemTypesCatalog } = queryItemTypes();
    const { data: queryData } = queryItemTypesWithLocations();
    const { data: incQueryData } = db.useQuery(
        open && stableItemId
            ? {
                  trailerCargoIncompatibilities: {
                      trailerItemType: {},
                      cargoItemType: {},
                  },
              }
            : null
    );

    const reactiveItemType = itemType ? queryData?.itemTypes?.find(it => it.id === itemType.id) || itemType : null;
    const isTrailerItem = (itemType?.category?.name ?? reactiveItemType?.category?.name) === TRAILERS_CATEGORY_NAME;

    const allItemTypes = useMemo(() => (itemTypesCatalog?.itemTypes ?? []) as ItemType[], [itemTypesCatalog?.itemTypes]);
    const trailerItemOptions = useMemo(
        () => allItemTypes.filter(t => t.category?.name === TRAILERS_CATEGORY_NAME && t.id !== stableItemId),
        [allItemTypes, stableItemId]
    );
    const cargoItemOptions = useMemo(() => allItemTypes.filter(t => t.category?.name !== TRAILERS_CATEGORY_NAME), [allItemTypes]);

    const incRowsForItem = useMemo(() => {
        const rows = (incQueryData?.trailerCargoIncompatibilities ?? []) as Array<{
            id?: string;
            trailerItemType?: { id: string } | null;
            cargoItemType?: { id: string } | null;
        }>;
        if (!stableItemId) return [];
        return rows.filter(r => r.trailerItemType?.id === stableItemId || r.cargoItemType?.id === stableItemId);
    }, [incQueryData?.trailerCargoIncompatibilities, stableItemId]);

    const selectedIncompatibleItems = useMemo((): ItemType[] => {
        if (!stableItemId) return [];
        const ids = isTrailerItem
            ? incRowsForItem.filter(r => r.trailerItemType?.id === stableItemId).map(r => r.cargoItemType?.id)
            : incRowsForItem.filter(r => r.cargoItemType?.id === stableItemId).map(r => r.trailerItemType?.id);
        const idSet = new Set(ids.filter(Boolean) as string[]);
        return allItemTypes.filter(t => idSet.has(t.id));
    }, [incRowsForItem, stableItemId, isTrailerItem, allItemTypes]);

    const persistIncompatibleSelection = useCallback(
        async (newSelection: ItemType[]) => {
            if (!canManageInventory || !stableItemId) return;
            const desired = new Set(newSelection.map(t => t.id));
            const txs: Parameters<typeof db.transact>[0] = [];
            if (isTrailerItem) {
                const currentRows = incRowsForItem.filter(r => r.trailerItemType?.id === stableItemId);
                const currentIds = new Set(currentRows.map(r => r.cargoItemType?.id).filter(Boolean) as string[]);
                for (const cid of currentIds) {
                    if (!desired.has(cid)) {
                        const row = currentRows.find(r => r.cargoItemType?.id === cid);
                        if (row?.id) txs.push(db.tx.trailerCargoIncompatibilities[row.id].delete());
                    }
                }
                for (const cid of desired) {
                    if (!currentIds.has(cid)) {
                        const rowId = instantId();
                        txs.push(
                            db.tx.trailerCargoIncompatibilities[rowId]
                                .update({ createdAt: new Date() })
                                .link({ trailerItemType: stableItemId })
                                .link({ cargoItemType: cid })
                        );
                    }
                }
            } else {
                const currentRows = incRowsForItem.filter(r => r.cargoItemType?.id === stableItemId);
                const currentIds = new Set(currentRows.map(r => r.trailerItemType?.id).filter(Boolean) as string[]);
                for (const tid of currentIds) {
                    if (!desired.has(tid)) {
                        const row = currentRows.find(r => r.trailerItemType?.id === tid);
                        if (row?.id) txs.push(db.tx.trailerCargoIncompatibilities[row.id].delete());
                    }
                }
                for (const tid of desired) {
                    if (!currentIds.has(tid)) {
                        const rowId = instantId();
                        txs.push(
                            db.tx.trailerCargoIncompatibilities[rowId]
                                .update({ createdAt: new Date() })
                                .link({ trailerItemType: tid })
                                .link({ cargoItemType: stableItemId })
                        );
                    }
                }
            }
            if (txs.length > 0) await db.transact(txs);
        },
        [canManageInventory, stableItemId, isTrailerItem, incRowsForItem]
    );

    if (!reactiveItemType) return null;

    const itemTypeWithLocations = reactiveItemType as ItemTypeWithLocations;
    const locations =
        'itemLocations' in itemTypeWithLocations
            ? ((itemTypeWithLocations.itemLocations || []) as Array<{
                  id: string;
                  quantity: number;
                  event: { id: string; name: string } | null | undefined;
              }>)
            : [];
    const locationData = calculateItemLocations(reactiveItemType, locations);
    const category = reactiveItemType.category;
    const categoryColor = category?.color || '#2196F3';

    const handleEventClick = (eventId: string) => {
        navigate(`/event/${eventId}`);
        onClose();
    };

    const content = (
        <Box>
            {/* Header */}
            <Box sx={{ position: 'relative', mb: 2 }}>
                {reactiveItemType.photoUrl && (
                    <Box
                        component="img"
                        src={reactiveItemType.photoUrl}
                        alt={reactiveItemType.name}
                        sx={{
                            width: '100%',
                            maxHeight: 300,
                            objectFit: 'contain',
                            borderRadius: 1,
                            mb: 2,
                        }}
                    />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h4" sx={{ flex: 1 }}>
                        {reactiveItemType.name}
                    </Typography>
                    {category && (
                        <Chip
                            label={category.name}
                            sx={{
                                bgcolor: categoryColor,
                                color: 'white',
                            }}
                        />
                    )}
                    {reactiveItemType.consumable && <Chip label="Consumable" size="small" color="secondary" />}
                </Box>
            </Box>

            {/* Description */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Description
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {reactiveItemType.description || 'No description'}
                </Typography>
            </Box>

            {/* Location Breakdown */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Location Breakdown
                </Typography>
                {locationData.total === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No inventory
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableBody>
                            {locationData.storageLocations.map(storage => (
                                <TableRow key={storage.id}>
                                    <TableCell>{storage.name}</TableCell>
                                    <TableCell align="right">{storage.quantity}</TableCell>
                                </TableRow>
                            ))}
                            {locationData.events.map(event => (
                                <TableRow key={event.eventId}>
                                    <TableCell>
                                        <Button
                                            variant="text"
                                            onClick={() => handleEventClick(event.eventId)}
                                            sx={{ textTransform: 'none', p: 0 }}
                                        >
                                            {event.eventName}
                                        </Button>
                                    </TableCell>
                                    <TableCell align="right">{event.quantity}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ fontWeight: 'bold' }}>
                                <TableCell>Total</TableCell>
                                <TableCell align="right">{locationData.total}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                )}
            </Box>

            {/* History */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    History
                </Typography>
                <HistorySummary itemTypeId={reactiveItemType.id} />
                <Box sx={{ mt: 2 }}>
                    <InventoryHistoryList itemTypeId={reactiveItemType.id} />
                </Box>
            </Box>

            {/* Trailer/cargo restrictions (trip planning) */}
            {canManageInventory && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {isTrailerItem ? 'Cargo restrictions' : 'Trailer restrictions'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {isTrailerItem ? 'Items incompatible with this trailer.' : 'Trailers incompatible with this item.'}
                    </Typography>
                    <Autocomplete
                        multiple
                        options={[...(isTrailerItem ? cargoItemOptions : trailerItemOptions)].sort((a, b) => {
                            const catA = (a as ItemType & { category?: { name?: string } }).category?.name ?? '';
                            const catB = (b as ItemType & { category?: { name?: string } }).category?.name ?? '';
                            const cmp = catA.localeCompare(catB);
                            return cmp !== 0 ? cmp : (a.name ?? '').localeCompare(b.name ?? '');
                        })}
                        value={selectedIncompatibleItems}
                        onChange={(_e, v) => {
                            void persistIncompatibleSelection(v);
                        }}
                        getOptionLabel={opt => opt.name ?? opt.id}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        groupBy={opt => (opt as ItemType & { category?: { name?: string } }).category?.name ?? 'Uncategorized'}
                        disabled={!canManageInventory}
                        renderGroup={params => (
                            <Box key={params.key}>
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        px: 1.5,
                                        py: 0.5,
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                    }}
                                >
                                    {params.group}
                                </Typography>
                                {params.children}
                            </Box>
                        )}
                        renderOption={(props, opt) => {
                            const consumable = (opt as ItemType & { consumable?: boolean }).consumable ?? false;
                            return (
                                <li {...props} key={opt.id}>
                                    {consumable && (
                                        <LocalGasStationIcon sx={{ fontSize: 16, color: 'warning.main', mr: 1, flexShrink: 0 }} />
                                    )}
                                    <span>{opt.name ?? opt.id}</span>
                                </li>
                            );
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((opt, index) => {
                                const color = (opt as ItemType & { category?: { color?: string } }).category?.color || '#2196F3';
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={key}
                                        {...tagProps}
                                        label={opt.name ?? opt.id}
                                        size="small"
                                        sx={{ bgcolor: color, color: 'white' }}
                                    />
                                );
                            })
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={isTrailerItem ? 'Incompatible cargo' : 'Incompatible trailers'}
                                placeholder="Search items…"
                            />
                        )}
                    />
                </Box>
            )}

            {/* Quick Actions */}
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Quick Actions
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {onEdit && (
                        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => onEdit(reactiveItemType)}>
                            Edit
                        </Button>
                    )}
                    {onDelete && (
                        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(reactiveItemType)}>
                            Delete
                        </Button>
                    )}
                    {onRecordPurchase && (
                        <Button variant="outlined" startIcon={<ShoppingCartIcon />} onClick={() => onRecordPurchase(reactiveItemType)}>
                            Purchase
                        </Button>
                    )}
                    {onRecordLostOrBroken && (
                        <Button variant="outlined" startIcon={<WarningAmberIcon />} onClick={() => onRecordLostOrBroken(reactiveItemType)}>
                            Lost / Broken
                        </Button>
                    )}
                    {onAdjustQuantity && (
                        <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => onAdjustQuantity(reactiveItemType)}>
                            Adjust Quantity
                        </Button>
                    )}
                    {onRecordConsumption && reactiveItemType.consumable && (
                        <Button
                            variant="outlined"
                            startIcon={<LocalGasStationIcon />}
                            onClick={() => onRecordConsumption(reactiveItemType)}
                        >
                            Consume
                        </Button>
                    )}
                </Stack>
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                PaperProps={{ sx: { maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
            >
                <Box sx={{ p: 2, position: 'relative' }}>
                    <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                    {content}
                </Box>
            </Drawer>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography component="span" variant="h6">
                    Item Details
                </Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>{content}</DialogContent>
        </Dialog>
    );
}
